import { useCallback, useEffect, useState } from "react";
import { FileStack, Printer, X, PackageCheck } from "lucide-react";
import api, { fmtDate, fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Manifests() {
  const [eligible, setEligible] = useState(null);
  const [manifests, setManifests] = useState(null);
  const [selected, setSelected] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/ops/manifest/eligible").then(({ data }) => setEligible(data)).catch(() => setEligible([]));
    api.get("/ops/manifests").then(({ data }) => setManifests(data)).catch(() => setManifests([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (sid) => setSelected((prev) => prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]);
  const allIds = (eligible || []).map((s) => s.shipment_id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));

  const create = async () => {
    if (!selected.length) return toast.error("Select at least one shipment to manifest");
    setBusy(true);
    try {
      const { data } = await api.post("/ops/manifest", { shipment_ids: selected, idempotency_key: crypto.randomUUID() });
      toast.success(`Manifest created — ${data.count} package(s), courier ${data.courier_code}`);
      setSelected([]);
      load();
    } catch (e) {
      const problems = e.response?.data?.detail?.problems;
      if (Array.isArray(problems)) problems.slice(0, 5).forEach((p) => toast.error(p));
      else toast.error(fmtErr(e));
      load();
    } finally { setBusy(false); }
  };

  const openDetail = async (m) => {
    try {
      const { data } = await api.get(`/ops/manifest/${m.manifest_id}`);
      setDetail(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="manifests-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Manifests</h1>
          <p className="text-sm text-slate-500">Group ready shipments into a pickup manifest for courier handover. Only AWB-assigned, un-manifested shipments are eligible.</p>
        </div>
        <button onClick={create} disabled={busy || !selected.length} data-testid="create-manifest-btn"
          className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue disabled:opacity-50">
          <FileStack className="h-4 w-4" /> {busy ? "Creating…" : `Create Manifest (${selected.length})`}
        </button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="manifest-eligible-table">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h2 className="text-sm font-bold text-shipany-navy">Eligible for Manifest</h2>
          {allIds.length > 0 && (
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              <input type="checkbox" data-testid="manifest-select-all" checked={allSelected}
                onChange={(e) => setSelected(e.target.checked ? allIds : [])} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
              Select all ({allIds.length})
            </label>
          )}
        </div>
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-widest text-slate-400">
              <th className="px-5 py-2.5"></th><th className="px-4 py-2.5">AWB</th><th className="px-4 py-2.5">Order</th>
              <th className="px-4 py-2.5">Courier</th><th className="px-4 py-2.5">Destination</th>
              <th className="px-4 py-2.5">Weight</th><th className="px-4 py-2.5">Mode</th><th className="px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {(eligible || []).map((s) => (
              <tr key={s.shipment_id} className="border-t border-slate-100" data-testid={`manifest-eligible-${s.awb}`}>
                <td className="px-5 py-3"><input type="checkbox" data-testid={`manifest-select-${s.awb}`} checked={selected.includes(s.shipment_id)} onChange={() => toggle(s.shipment_id)} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" /></td>
                <td className="mono px-4 py-3 font-semibold text-shipany-blue">{s.awb}</td>
                <td className="mono px-4 py-3 text-xs">{s.order_number || "—"}</td>
                <td className="px-4 py-3 text-xs">{s.courier_name || "—"}</td>
                <td className="px-4 py-3 text-xs">{s.destination_city} — {s.delivery_pincode}</td>
                <td className="mono px-4 py-3 text-xs">{s.weight} kg</td>
                <td className="px-4 py-3 text-xs">{s.payment_mode}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-[11px] font-bold text-blue-700">{s.status}</span></td>
              </tr>
            ))}
            {eligible && eligible.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-10 text-center text-slate-400">No shipments ready to manifest. Book shipments and generate AWBs first.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-8" data-testid="manifest-list">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-shipany-navy"><PackageCheck className="h-5 w-5 text-shipany-blue" /> Manifest History</h2>
        <div className="mt-3 space-y-2.5">
          {(manifests || []).map((m) => (
            <div key={m.manifest_id} className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white px-5 py-3.5" data-testid={`manifest-row-${m.manifest_id}`}>
              <p className="mono font-semibold text-shipany-navy">{m.manifest_id}</p>
              <p className="text-sm text-slate-600">{m.count} package(s) · {m.courier_code}</p>
              <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold uppercase text-emerald-600">{m.status}</span>
              <p className="ml-auto text-xs text-slate-400">{fmtDateTime(m.created_at)}</p>
              <button onClick={() => openDetail(m)} data-testid={`manifest-view-${m.manifest_id}`}
                className="rounded-lg bg-shipany-blue/10 px-3.5 py-1.5 text-xs font-bold text-shipany-blue hover:bg-shipany-blue hover:text-white">View AWBs</button>
              <a href={`${API_BASE}/api/ops/manifest/${m.manifest_id}/document`} target="_blank" rel="noreferrer" data-testid={`manifest-print-${m.manifest_id}`}
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:border-shipany-blue hover:text-shipany-blue">
                <Printer className="h-3.5 w-3.5" /> Download / Print
              </a>
            </div>
          ))}
          {manifests && manifests.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">No manifests yet.</div>}
        </div>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="manifest-detail-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">Manifest {detail.manifest_id}</h2>
              <button onClick={() => setDetail(null)} data-testid="manifest-detail-close" className="text-slate-400"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 text-xs text-slate-500">{detail.count} package(s) · courier {detail.courier_code} · created {fmtDateTime(detail.created_at)} · status {detail.status}</p>
            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full min-w-[560px] text-sm">
                <thead><tr className="bg-slate-50 text-left text-[10px] font-bold uppercase tracking-widest text-slate-400"><th className="px-4 py-2.5">AWB</th><th className="px-4 py-2.5">Order</th><th className="px-4 py-2.5">Destination</th><th className="px-4 py-2.5">Weight</th><th className="px-4 py-2.5">Status</th></tr></thead>
                <tbody>
                  {(detail.shipments || []).map((s) => (
                    <tr key={s.shipment_id} className="border-t border-slate-100" data-testid={`manifest-awb-${s.awb}`}>
                      <td className="mono px-4 py-2.5 font-semibold text-shipany-blue">{s.awb}</td>
                      <td className="mono px-4 py-2.5 text-xs">{s.order_number || "—"}</td>
                      <td className="px-4 py-2.5 text-xs">{s.destination_city} — {s.delivery_pincode}</td>
                      <td className="mono px-4 py-2.5 text-xs">{s.weight} kg</td>
                      <td className="px-4 py-2.5 text-xs">{s.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <a href={`${API_BASE}/api/ops/manifest/${detail.manifest_id}/document`} target="_blank" rel="noreferrer" data-testid="manifest-detail-print"
              className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-shipany-navy py-3 text-sm font-bold text-white hover:bg-shipany-blue">
              <Printer className="h-4 w-4" /> Print / Save PDF
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
